import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { DataItem, updateDailyMachine } from '../../services/apiSLTP/activities';
import { createDailyMachine, DailyMachine, deleteDailyMachine } from '../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getPriceTypeInfos, materialState, stepModes, systemConfigKeyEnum } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import ManageDailyMachineModal from '../modals/operations/planning/ManageDailyMachineModal';
import IconButton from '../shared/IconButton';

interface MachinesCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    refetch?: () => void;
    hiddenPlusIcon?: boolean;
    createTransfer?: boolean;
}

const MachinesCell: React.FC<MachinesCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    refetch = () => {},
    hiddenPlusIcon = false,
    createTransfer = true,
}) => {
    const itemType = 'MACHINES';
    const { getConfigValue } = useSystemConfig();
    const { t } = useTranslation();
    const manageModal = useBoolean(false);
    const [machine, setMachine] = useState<DailyMachine | null>(null);

    const countDuplicateMachine = (machineId: string) => {
        const matchingMachineIds = activities
            .flatMap((activity) => activity.dailyMachines)
            .filter((dailyMachine) => dailyMachine.machineId._id === machineId);
        if (matchingMachineIds.length === 1) return 1;
        if (
            matchingMachineIds.length === 2 &&
            (matchingMachineIds[0].state === materialState.NEW || matchingMachineIds[1].state === materialState.NEW) &&
            (matchingMachineIds[0].state === materialState.REMOVE || matchingMachineIds[1].state === materialState.REMOVE)
        )
            return 1;
        return matchingMachineIds.length;
    };

    const handleMachinesChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailyMachine(fromItemId, { activityId: toLineId, createTransfer: true }, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error updating daily machine:', error);
        }
    };

    const handleDeleteMachine = async (id: string, index: number) => {
        try {
            await deleteDailyMachine(id, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error deleting daily machine:', error);
        }
    };

    const handleCreateMachine = async (dailyMachineId: string) => {
        const selectedMachine = activity.dailyMachines.find((value) => value._id === dailyMachineId);
        if (selectedMachine) {
            const response = await createDailyMachine(activity._id, selectedMachine.machineId._id, selectedMachine.state, createTransfer);
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleMachinesChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                size="small"
                                onClick={() => {
                                    setMachine(null);
                                    manageModal.setTrue();
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addMachine'))}
                            />
                        )}
                    </div>
                    <div className=" gap-5px fx-c fx-fg-1">
                        {activity.dailyMachines.map((machine, index) => (
                            <div key={`${activityIndex}-${index}`} className=" gap-5px fx-r fx-fg-1 fx-items-fs">
                                <DraggableCell
                                    dragIds={{ itemId: machine._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDeleteMachine(machine._id, index)}
                                    disabled={disabled}
                                    state={machine.state}
                                    onDuplicate={() => handleCreateMachine(machine._id)}
                                    warning={countDuplicateMachine(machine.machineId._id) > 1}
                                    warningChar={countDuplicateMachine(machine.machineId._id).toString()}
                                    onDoubleClick={() => {
                                        setMachine(machine);
                                        manageModal.setTrue();
                                    }}
                                >
                                    <div className="one-line-container">
                                        <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                            <div className="one-line" title={machine.machineId.designation}>
                                                {machine.machineId.designation}
                                            </div>
                                        </div>
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                                <div className="one-line" title={machine.machineId.codeProC}>
                                                    {machine.machineId.codeProC}
                                                </div>
                                            </div>
                                        )}

                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div
                                                className="one-line font-italic"
                                                style={{
                                                    color: '#DC4D23',
                                                    textDecorationLine: machine?.isUsed && activity.includedInExpenses ? undefined : 'line-through',
                                                }}
                                            >
                                                {machine.price ?? machine.machineId.price ?? machine.machineId.machineTypeId.price}
                                                {getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}/
                                                {capitalizeFirstLetter(
                                                    t(
                                                        getPriceTypeInfos(
                                                            machine.priceType ??
                                                                machine.machineId.priceType ??
                                                                machine.machineId.machineTypeId.priceType
                                                        )
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </DraggableCell>
                                {(activity.stepMode === stepModes.REPORT || activity.stepMode === stepModes.VALIDATE) && (
                                    <div className="h-25px fx-r fx-items-c">
                                        <Checkbox
                                            checked={machine.isUsed}
                                            onChange={async (_, checked) => {
                                                await updateDailyMachine(machine._id, { isUsed: checked });
                                                refetch();
                                            }}
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                            title={capitalizeFirstLetter(t('isUsed'))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>
            {manageModal.value && (
                <ManageDailyMachineModal
                    open={manageModal.value}
                    handleClose={manageModal.setFalse}
                    refetch={refetch}
                    activityId={activity._id}
                    dailyMachine={machine}
                    machine={null}
                    activities={activities}
                    createTransfer={createTransfer}
                    stepMode={activity.stepMode}
                />
            )}
        </div>
    );
};

export default MachinesCell;
